import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCcw, Send, Settings2, UserPlus, UserCheck, AlertTriangle, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { Dialog, EmptyState, ErrorState, TableSkeleton, StatusPill, money, fmtDate, plural } from '../../components/ui';
import { Notice } from '../Financials/financeKit';

// Invoices › Xero: send hourly (and other non-weekly) invoices to Xero the way Brevity does:
// pick a date range, see every unsent invoice with its bill-to contact and anything that would
// stop it, tick, send. Drafts by default, never twice. Backend: routes/xeroSync.js.

const pad = (n) => String(n).padStart(2, '0');
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoOf(d); };

const CONTACT = {
  linked: { icon: UserCheck, cls: 'text-emerald-700', text: (c) => c.name },
  found: { icon: UserCheck, cls: 'text-emerald-700', text: (c) => c.name },
  create: { icon: UserPlus, cls: 'text-indigo-700', text: (c) => `New contact: ${c.name}` },
  missing: { icon: AlertTriangle, cls: 'text-rose-700', text: () => 'No Xero contact' },
};

const XERO_TONE = { PAID: 'success', AUTHORISED: 'info', SUBMITTED: 'info', DRAFT: 'neutral', VOIDED: 'danger', DELETED: 'danger' };

function XeroInvoicesView() {
  const confirm = useConfirm();
  const [range, setRange] = useState({ from: daysAgo(30), to: isoOf(new Date()) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState('');
  const [status, setStatus] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [contactFor, setContactFor] = useState(null);
  const [showSent, setShowSent] = useState(false);

  useEffect(() => { api.get('/xero-sync/status').then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false })); }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await api.get(`/xero-sync/invoices/preview?from=${range.from}&to=${range.to}`);
      setData(d);
      setPicked(new Set(d.rows.filter((r) => !r.skipped && !r.errors.length).map((r) => r.id)));
    } catch (e) { setError(e.response?.data?.message || 'Could not load the invoices.'); }
    finally { setLoading(false); }
  }, [range.from, range.to]);

  useEffect(() => { if (status?.connected) load(); }, [status, load]);

  const rows = data?.rows || [];
  const ready = rows.filter((r) => !r.skipped && !r.errors.length);
  const chosen = ready.filter((r) => picked.has(r.id));
  const chosenTotal = chosen.reduce((s, r) => s + r.total, 0);
  const asWord = data?.sendAs === 'AUTHORISED' ? 'approved invoices' : 'drafts';

  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setPicked((p) => (p.size === ready.length ? new Set() : new Set(ready.map((r) => r.id))));

  const send = async () => {
    const creates = chosen.filter((r) => r.contact?.how === 'create').length;
    const ok = await confirm(
      `${plural(chosen.length, 'invoice')} totalling ${money(chosenTotal)} go to Xero as ${asWord}.${creates ? ` ${plural(creates, 'new contact')} will be created in Xero.` : ''}`,
      { title: `Send ${plural(chosen.length, 'invoice')} to Xero?`, confirmLabel: 'Send to Xero' });
    if (!ok) return;
    setBusy('send');
    try {
      const { data: d } = await api.post('/xero-sync/invoices/push', { invoiceIds: chosen.map((r) => r.id) });
      const good = d.results.filter((r) => r.ok);
      const bad = d.results.filter((r) => !r.ok);
      if (good.length) toast.success(`In Xero: ${good.map((r) => r.xeroInvoiceNumber).filter(Boolean).join(', ') || plural(good.length, 'invoice')}`);
      if (bad.length) toast.error(`${plural(bad.length, 'invoice')} not sent. ${bad[0].error}`, { duration: 9000 });
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Sending to Xero failed'); }
    finally { setBusy(''); }
  };

  const refresh = async () => {
    setBusy('refresh');
    try {
      const { data: d } = await api.post('/xero-sync/invoices/refresh');
      const bits = [];
      if (d.paid) bits.push(`${plural(d.paid, 'invoice')} now paid`);
      if (d.partlyPaid) bits.push(`${d.partlyPaid} part-paid`);
      if (d.voidedInXero) bits.push(`${d.voidedInXero} voided in Xero (check them)`);
      toast.success(bits.length ? bits.join(', ') : `Checked ${plural(d.checked, 'invoice')}. Nothing changed.`);
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not read the status from Xero'); }
    finally { setBusy(''); }
  };

  if (status && !status.connected) {
    return (
      <div className="card p-6">
        <EmptyState icon={Send} title="Xero is not connected"
          hint="Connect Xero to send invoices and timesheets to it."
          action={<Link to="/settings?tab=integrations" className="btn-primary btn-sm">Go to integrations</Link>} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card p-5 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center shrink-0 font-bold text-[#13B5EA]" aria-hidden="true">X</span>
          <div className="flex-1 min-w-[220px]">
            <h3 className="text-base font-semibold text-slate-900">Send invoices to Xero</h3>
            <p className="text-sm text-slate-600 mt-1 max-w-[72ch]">
              Every invoice not yet in Xero{status?.tenantName ? ` (${status.tenantName})` : ''}, with who it will be addressed to and anything that would stop it.
              They go as {asWord}, and an invoice is never sent twice. Weekly fixed invoices are sent from Weekly fixed.
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary btn-sm" onClick={refresh} disabled={!!busy}>
              {busy === 'refresh' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <RefreshCcw size={13} aria-hidden="true" />} Get paid status
            </button>
            <button type="button" className="btn-secondary btn-sm" onClick={() => setSettingsOpen(true)}>
              <Settings2 size={13} aria-hidden="true" /> Xero settings
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-slate-100">
          <div>
            <label htmlFor="xi-from" className="text-[13px] text-slate-700 font-medium block mb-1">Invoice date from</label>
            <input id="xi-from" type="date" className="input-field" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
          </div>
          <div>
            <label htmlFor="xi-to" className="text-[13px] text-slate-700 font-medium block mb-1">to</label>
            <input id="xi-to" type="date" className="input-field" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
          </div>
          <button type="button" className="btn-secondary" onClick={load} disabled={loading}>
            {loading ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <RefreshCcw size={14} aria-hidden="true" />} Show invoices
          </button>
        </div>
      </div>

      {data?.needsReconnect && (
        <Notice tone="amber" title="Reconnect Xero to check account codes"
          actions={<Link to="/settings?tab=integrations" className="btn-secondary btn-sm">Go to integrations</Link>}>
          Rostery can't read your Xero income accounts yet, so it can't warn about a wrong account code before sending.
          In Settings › Integrations, press Reconnect on Xero to give that permission.
        </Notice>
      )}

      <div className="card overflow-hidden">
        {loading && !data ? <TableSkeleton rows={4} cols={6} />
          : error ? <ErrorState message={error} onRetry={load} />
          : !rows.length ? <EmptyState icon={Send} title="Nothing to send" hint={`Every invoice dated ${fmtDate(range.from)} to ${fmtDate(range.to)} is already in Xero, or there are none.`} />
          : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                    <tr>
                      <th className="px-4 py-2.5 w-10">
                        <input type="checkbox" aria-label="Choose all that can be sent" checked={ready.length > 0 && picked.size === ready.length} onChange={toggleAll} />
                      </th>
                      <th className="px-3 py-2.5 font-medium">Invoice</th>
                      <th className="px-3 py-2.5 font-medium">Participant</th>
                      <th className="px-3 py-2.5 font-medium">Pays</th>
                      <th className="px-3 py-2.5 font-medium">Xero contact</th>
                      <th className="px-3 py-2.5 font-medium text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((r) => {
                      const c = CONTACT[r.contact?.how] || CONTACT.missing;
                      const Icon = c.icon;
                      const blocked = r.skipped || r.errors.length > 0;
                      return (
                        <tr key={r.id} className={blocked ? 'bg-rose-50/30 align-top' : 'align-top'}>
                          <td className="px-4 py-3">
                            <input type="checkbox" aria-label={`Send ${r.invoiceNumber}`} disabled={blocked} checked={picked.has(r.id)} onChange={() => toggle(r.id)} />
                          </td>
                          <td className="px-3 py-3">
                            <p className="font-medium text-slate-900 whitespace-nowrap">{r.invoiceNumber}</p>
                            <p className="text-[12px] text-slate-500">{fmtDate(r.date)} · {plural(r.lineCount || 0, 'line')}{r.status === 'draft' ? ' · draft' : ''}</p>
                          </td>
                          <td className="px-3 py-3 text-slate-800">
                            {r.client}
                            {r.errors.map((e) => <p key={e} className="text-[12px] text-rose-700 mt-1">{e}</p>)}
                            {r.warnings.map((w) => <p key={w} className="text-[12px] text-amber-700 mt-1">{w}</p>)}
                          </td>
                          <td className="px-3 py-3 text-slate-700 whitespace-nowrap">{r.payerLabel}</td>
                          <td className="px-3 py-3">
                            {!r.skipped && (
                              <div className="flex flex-col items-start gap-1">
                                <span className={`inline-flex items-center gap-1 text-[13px] ${c.cls}`}><Icon size={13} aria-hidden="true" />{c.text(r.contact || {})}</span>
                                <button type="button" className="text-[12px] text-indigo-700 hover:text-indigo-900"
                                  onClick={() => setContactFor({ clientId: r.clientId, client: r.client, kind: r.contact?.kind || 'participant' })}>
                                  {r.contact?.how === 'missing' ? 'Choose contact' : 'Change'}
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right font-semibold text-slate-900 tabular-nums">{money(r.total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                <p className="text-sm text-slate-600 flex-1">
                  {plural(ready.length, 'invoice')} ready{rows.length > ready.length ? `, ${rows.length - ready.length} need attention` : ''}.
                  {chosen.length ? <> Chosen: <strong className="text-slate-900 tabular-nums">{money(chosenTotal)}</strong></> : null}
                </p>
                <button type="button" className="btn-primary" disabled={!chosen.length || !!busy} onClick={send}>
                  {busy === 'send' ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
                  Send {plural(chosen.length, 'invoice')} to Xero
                </button>
              </div>
            </>
          )}
      </div>

      {data?.sent?.length > 0 && (
        <div className="card overflow-hidden">
          <button type="button" className="w-full flex items-center justify-between px-4 py-3 text-left" aria-expanded={showSent} onClick={() => setShowSent((v) => !v)}>
            <span className="text-sm font-semibold text-slate-800">Already in Xero ({data.sent.length})</span>
            <ChevronDown size={15} className={showSent ? 'rotate-180 transition-transform' : 'transition-transform'} aria-hidden="true" />
          </button>
          {showSent && (
            <div className="overflow-x-auto border-t border-slate-100">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                  <tr><th className="px-4 py-2 font-medium">Invoice</th><th className="px-3 py-2 font-medium">Participant</th><th className="px-3 py-2 font-medium">In Xero</th><th className="px-3 py-2 font-medium">Rostery</th><th className="px-3 py-2 font-medium text-right">Amount</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.sent.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-2 whitespace-nowrap">{r.invoiceNumber} <span className="text-slate-400">· {fmtDate(r.date)}</span></td>
                      <td className="px-3 py-2">{r.client}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.xeroNumber || '—'} {r.xeroStatus && <StatusPill tone={XERO_TONE[r.xeroStatus] || 'neutral'} label={r.xeroStatus.toLowerCase()} className="ml-1" />}</td>
                      <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {settingsOpen && <InvoiceSettingsDialog onClose={() => setSettingsOpen(false)} onSaved={load} />}
      {contactFor && <ContactDialog who={contactFor} onClose={() => setContactFor(null)} onSaved={() => { setContactFor(null); load(); }} />}
    </div>
  );
}

function ContactDialog({ who, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/xero-sync/invoices/contact', { clientId: who.clientId, kind: who.kind, contactName: name });
      toast.success(`${who.client}: invoices go to ${data.contactName}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={onClose} size="sm" title={who.kind === 'plan_manager' ? `Plan manager contact for ${who.client}` : `Xero contact for ${who.client}`}
      description="Type the contact's name exactly as it is in Xero. Every invoice for this payer goes to it from now on."
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" disabled={!name.trim() || saving} onClick={save}>
          {saving && <Loader2 size={14} className="animate-spin" aria-hidden="true" />} Use this contact
        </button>
      </>}>
      <label htmlFor="xi-contact" className="text-[13px] text-slate-700 font-medium block mb-1">Xero contact name</label>
      <input id="xi-contact" className="input-field" value={name} onChange={(e) => setName(e.target.value)} placeholder={who.kind === 'plan_manager' ? 'e.g. NDSP Plan Managers' : who.client} />
    </Dialog>
  );
}

// NDIS support categories, by the first two digits of the support item number.
const CATEGORIES = [
  ['01', 'Daily activities (incl. SIL)'], ['02', 'Transport'], ['03', 'Consumables'], ['04', 'Social and community participation'],
  ['05', 'Assistive technology'], ['07', 'Support coordination'], ['08', 'Improved living arrangements'], ['09', 'Increased social and community participation'],
  ['10', 'Finding and keeping a job'], ['11', 'Improved relationships'], ['12', 'Improved health and wellbeing'], ['13', 'Improved learning'],
  ['14', 'Improved life choices'], ['15', 'Improved daily living'],
];
const PAYERS = [['ndia', 'NDIA-managed'], ['plan_manager', 'Plan-managed'], ['self_managed', 'Self-managed'], ['client', 'Private'], ['services_australia', 'Services Australia'], ['aged_care_contribution', 'Client contribution']];

function InvoiceSettingsDialog({ onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/xero-sync/settings').then((r) => setForm(r.data.invoice)).catch(() => toast.error('Could not load the settings'));
    api.get('/xero-sync/meta').then((r) => setMeta(r.data)).catch(() => setMeta({ accounts: [], taxRates: [], needsReconnect: true }));
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAcct = (k, v) => setForm((f) => ({ ...f, accounts: { ...f.accounts, [k]: v } }));
  const save = async () => {
    setSaving(true);
    try { await api.put('/xero-sync/settings', { invoice: form }); toast.success('Xero settings saved'); onSaved(); onClose(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
    finally { setSaving(false); }
  };
  const accounts = meta?.accounts || [];
  // Plain render helpers, not components: a component defined in here would remount on every
  // keystroke and drop the focus from its text box.
  const accountPick = (id, value, onChange, blank) => (accounts.length ? (
    <select id={id} className="input-field" value={value || ''} onChange={(e) => onChange(e.target.value)}>
      {blank && <option value="">{blank}</option>}
      {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
    </select>
  ) : <input id={id} className="input-field" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={blank ? 'Same as default' : '200'} />);
  const taxes = meta?.taxRates || [];
  const taxPick = (id, value, onChange) => (taxes.length ? (
    <select id={id} className="input-field" value={value || ''} onChange={(e) => onChange(e.target.value)}>
      {taxes.map((t) => <option key={t.type} value={t.type}>{t.name}</option>)}
    </select>
  ) : <input id={id} className="input-field" value={value || ''} onChange={(e) => onChange(e.target.value)} />);
  const lbl = 'text-[13px] text-slate-700 font-medium block mb-1';

  return (
    <Dialog open onClose={onClose} size="lg" title="Xero settings for invoices"
      description="Set once. Rostery uses these every time it sends an invoice."
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" disabled={!form || saving} onClick={save}>
          {saving && <Loader2 size={14} className="animate-spin" aria-hidden="true" />} Save settings
        </button>
      </>}>
      {!form ? <TableSkeleton rows={4} cols={2} /> : (
        <div className="space-y-5">
          {meta?.needsReconnect && (
            <Notice tone="amber" title="Type the account codes yourself for now">
              Rostery can't list your Xero accounts until Xero is reconnected (Settings › Integrations › Xero › Reconnect).
            </Notice>
          )}
          <fieldset>
            <legend className={lbl}>Send invoices to Xero as</legend>
            <div className="flex gap-4 text-sm">
              <label className="inline-flex items-center gap-2"><input type="radio" checked={form.sendAs !== 'AUTHORISED'} onChange={() => set('sendAs', 'DRAFT')} /> Drafts: you approve them in Xero</label>
              <label className="inline-flex items-center gap-2"><input type="radio" checked={form.sendAs === 'AUTHORISED'} onChange={() => set('sendAs', 'AUTHORISED')} /> Approved invoices</label>
            </div>
          </fieldset>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><label htmlFor="xs-def" className={lbl}>Default income account</label>{accountPick('xs-def', form.defaultAccount, (v) => set('defaultAccount', v))}</div>
            <div><label htmlFor="xs-free" className={lbl}>GST-free tax code (NDIS)</label>{taxPick('xs-free', form.taxFree, (v) => set('taxFree', v))}</div>
            <div><label htmlFor="xs-gst" className={lbl}>GST tax code (private)</label>{taxPick('xs-gst', form.taxGst, (v) => set('taxGst', v))}</div>
          </div>
          <div>
            <p className={lbl}>Income account by support category <span className="font-normal text-slate-500">(blank uses the default)</span></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              {CATEGORIES.map(([k, name]) => (
                <div key={k} className="grid grid-cols-[1fr_minmax(0,11rem)] items-center gap-2">
                  <label htmlFor={`xs-${k}`} className="text-[13px] text-slate-700"><span className="font-mono text-slate-400 mr-1">{k}</span>{name}</label>
                  {accountPick(`xs-${k}`, form.accounts?.[k], (v) => setAcct(k, v), 'Default')}
                </div>
              ))}
              {[['travel', 'Travel and mileage'], ['expense', 'Expenses'], ['cancellation', 'Cancellation charges']].map(([k, name]) => (
                <div key={k} className="grid grid-cols-[1fr_minmax(0,11rem)] items-center gap-2">
                  <label htmlFor={`xs-${k}`} className="text-[13px] text-slate-700">{name}</label>
                  {accountPick(`xs-${k}`, form.accounts?.[k], (v) => setAcct(k, v), 'Default')}
                </div>
              ))}
            </div>
          </div>
          <fieldset>
            <legend className={lbl}>Send these invoices to Xero</legend>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
              {PAYERS.map(([k, name]) => (
                <label key={k} className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={form.payers?.[k] !== false} onChange={(e) => set('payers', { ...form.payers, [k]: e.target.checked })} /> {name}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="inline-flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-1" checked={form.createContacts !== false} onChange={(e) => set('createContacts', e.target.checked)} />
              <span>Create a Xero contact for a participant who doesn't have one. Plan managers are never created: add them in Xero first.</span>
            </label>
            <div>
              <label htmlFor="xs-due" className={lbl}>Due date when the invoice has none (days)</label>
              <input id="xs-due" type="number" min="0" max="120" className="input-field" value={form.dueDays ?? 14} onChange={(e) => set('dueDays', e.target.value)} />
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}

// Invoices › Xero: send invoices, and the clients who are (or are not yet) Xero contacts.
export default function XeroInvoicesTab() {
  const [view, setView] = useState(() => (new URLSearchParams(window.location.search).get('view') === 'clients' ? 'clients' : 'invoices'));
  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="tablist" aria-label="Xero">
        {[['invoices', 'Invoices'], ['clients', 'Clients']].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => setView(id)}
            className={`px-3 py-1.5 text-sm rounded-md font-medium ${view === id ? 'bg-[#13B5EA] text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{label}</button>
        ))}
      </div>
      {view === 'invoices' ? <XeroInvoicesView /> : <XeroClientsView />}
    </div>
  );
}

const CLIENT_STATUS = {
  ready: { tone: 'success', label: 'Ready to add' },
  in_xero: { tone: 'info', label: 'In Xero' },
  missing_name: { tone: 'warning', label: 'Name incomplete' },
};
const TYPE_LABEL = { ndis_managed: 'NDIS', ndia_managed: 'NDIA-managed', plan_managed: 'Plan-managed', self_managed: 'Self-managed', aged_care: 'Aged care', support_coordination: 'Support coordination' };

// Rostery clients and whether each is a Xero contact; add the ones who are not.
function XeroClientsView() {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data: st } = await api.get('/xero-sync/status').catch(() => ({ data: { connected: false } }));
      if (!st?.connected) { setError('Xero is not connected. Connect it in Settings › Integrations.'); return; }
      const { data: d } = await api.get('/xero-sync/contacts/clients');
      setData(d);
      setPicked(new Set());
    } catch (e) { setError(e.response?.data?.message || 'Could not read your Xero contacts.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const clients = data?.clients || [];
  const ready = clients.filter((c) => c.status === 'ready');
  const chosen = ready.filter((c) => picked.has(c.clientId));
  const allPicked = ready.length > 0 && chosen.length === ready.length;
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setPicked(allPicked ? new Set() : new Set(ready.map((c) => c.clientId)));

  const add = async (list) => {
    if (!list.length) return;
    const ok = await confirm(
      `${plural(list.length, 'contact')} will be created in Xero with each client's name, NDIS number (as the account number), mobile, email and street address where Rostery has them.`,
      { title: `Add ${plural(list.length, 'client')} to Xero?`, confirmLabel: 'Add to Xero' });
    if (!ok) return;
    setBusy(true);
    try {
      const { data: r } = await api.post('/xero-sync/contacts/clients/add', { clientIds: list.map((c) => c.clientId) });
      const bad = r.results.filter((x) => !x.ok);
      const already = r.results.filter((x) => x.existing);
      if (r.added) toast.success(`${plural(r.added, 'client')} added to Xero`);
      if (already.length) toast.error(`Already in Xero, not created again: ${already.map((x) => x.name).join(', ')}`, { duration: 9000 });
      if (bad.length) toast.error(`${bad[0].name}: ${bad[0].error}${bad.length > 1 ? ` (+${bad.length - 1} more)` : ''}`, { duration: 9000 });
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Adding clients to Xero failed'); }
    finally { setBusy(false); }
  };

  if (error) return <div className="card"><ErrorState message={error} onRetry={load} /></div>;
  if (!data) return <div className="card"><TableSkeleton rows={5} cols={4} /></div>;
  return (
    <div className="space-y-4">
      <div className="card p-5 sm:p-6 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-900">Clients in Xero</h3>
          <button type="button" className="btn-primary" disabled={!ready.length || busy} onClick={() => add(ready)}>
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <UserPlus size={14} aria-hidden="true" />}
            {ready.length ? `Add all ${plural(ready.length, 'ready client')} to Xero` : 'Every client is in Xero'}
          </button>
        </div>
        <p className="text-sm text-slate-600 max-w-[72ch]">
          Invoices need each client as a Xero contact. Add the missing ones here: Rostery creates them with their name, NDIS number, mobile, email and address.
          A client already in Xero (same name, NDIS number or email) is linked, never added twice.
        </p>
      </div>
      <div className="card overflow-hidden">
        {!clients.length ? <EmptyState icon={UserPlus} title="No active clients" hint="Clients appear here once they are in Rostery." /> : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 w-10"><input type="checkbox" aria-label="Select every client ready to add" disabled={!ready.length} checked={allPicked} onChange={toggleAll} /></th>
                    <th className="px-3 py-2.5 font-medium">Client</th>
                    <th className="px-3 py-2.5 font-medium">Type</th>
                    <th className="px-3 py-2.5 font-medium">NDIS number</th>
                    <th className="px-3 py-2.5 font-medium">Xero</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {clients.map((c) => {
                    const st = CLIENT_STATUS[c.status] || CLIENT_STATUS.in_xero;
                    return (
                      <tr key={c.clientId} className="align-top">
                        <td className="px-4 py-3"><input type="checkbox" aria-label={`Add ${c.name}`} disabled={c.status !== 'ready'} checked={picked.has(c.clientId)} onChange={() => toggle(c.clientId)} /></td>
                        <td className="px-3 py-3"><p className="font-medium text-slate-900">{c.name}</p>{c.email && <p className="text-[12px] text-slate-500 break-all">{c.email}</p>}</td>
                        <td className="px-3 py-3 text-slate-700">{TYPE_LABEL[c.type] || c.type || <span className="text-slate-400">—</span>}</td>
                        <td className="px-3 py-3 text-slate-700 tabular-nums">{c.ndis || <span className="text-slate-400">—</span>}</td>
                        <td className="px-3 py-3">
                          <StatusPill tone={st.tone} label={st.label} />
                          {c.xeroName && c.xeroName !== c.name && <p className="text-[12px] text-slate-500 mt-1">As {c.xeroName}</p>}
                          {c.reason && <p className="text-[12px] text-slate-500 mt-1 max-w-[18rem]">{c.reason}</p>}
                          {c.note && <p className="text-[12px] text-slate-500 mt-1 max-w-[18rem]">{c.note}</p>}
                          {c.status === 'missing_name' && <Link to={`/clients/${c.clientId}`} className="text-[12px] text-sky-700 font-medium hover:underline">Fix in their profile</Link>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              <p className="text-sm text-slate-600 flex-1">{plural(clients.filter((c) => c.status === 'in_xero').length, 'client')} in Xero · {ready.length} ready to add.</p>
              <button type="button" className="btn-primary" disabled={!chosen.length || busy} onClick={() => add(chosen)}>
                {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <UserPlus size={14} aria-hidden="true" />}
                Add {plural(chosen.length, 'client')} to Xero
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
